/**
 * Service notifications in-app — app client (particulier).
 * Routes via buildApiUrl → API secondaire (/notifications).
 * Jamais importé par des pages web-only ; composants consommateurs guardés natif.
 *
 * Important : un même user_id peut être pro + client. On demande toujours
 * audience=client et on filtre aussi côté app les notifs backoffice.
 */

import { buildApiUrl, getNativeClientHeaders } from '../config/apiConfig';
import { mapProBookingPathToClientReservations } from '../utils/stayDeepLink';

const notificationsUrl = (path: string): string =>
  buildApiUrl(path.startsWith('/') ? path : `/${path}`);

const getHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...getNativeClientHeaders(),
  };
};

/** Types exclusivement destinés au backoffice pro (aligné API). */
export const PRO_ONLY_NOTIFICATION_TYPES = new Set([
  'new_booking',
  'forfait_purchased',
  'credit_pack_purchased',
  'event_registration',
  'event_unsubscription',
  'payment_received',
  'new_stay_booking',
  'subscription_cancelled',
  'boarding_subscription_cancelled',
]);

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  readAt: string | null;
  actionType: string | null;
  actionData: {
    path?: string;
    booking_id?: number;
    bookingId?: number;
    conversation_sid?: string;
    quote_id?: number;
    [key: string]: unknown;
  } | null;
  bookingId: number | null;
  forfaitId: number | null;
  eventId: number | null;
  messageId: string | null;
}

/** True si la notif est destinée au backoffice pro (à masquer sur le booking). */
export function isProFacingNotification(notification: AppNotification): boolean {
  const type = (notification.type || '').toLowerCase();
  if (PRO_ONLY_NOTIFICATION_TYPES.has(type)) return true;
  const path = String(notification.actionData?.path || '');
  if (mapProBookingPathToClientReservations(path)) return false;
  if (path.startsWith('/app/messages/')) return false;
  return path.startsWith('/app/') || path.startsWith('/backoffice/');
}

export function filterClientNotifications(
  notifications: AppNotification[],
): AppNotification[] {
  return notifications.filter((n) => !isProFacingNotification(n));
}

/** Résout la destination de navigation pour une notification client. */
export const resolveClientNotificationPath = (
  notification: AppNotification,
): string | null => {
  const type = (notification.type || '').toLowerCase();
  if (PRO_ONLY_NOTIFICATION_TYPES.has(type)) return null;

  const path = notification.actionData?.path;
  if (path) {
    if (path.startsWith('/app/messages/')) {
      const sid = path.replace('/app/messages/', '');
      return sid ? `/messaging?conversation=${sid}` : '/messaging';
    }
    const mappedBookingPath = mapProBookingPathToClientReservations(path);
    if (mappedBookingPath) return mappedBookingPath;
    if (path.startsWith('/app/') || path.startsWith('/backoffice/')) return null;
    if (path.startsWith('/')) return path;
    return `/${path}`;
  }

  if (notification.type === 'new_message') {
    const sid =
      notification.actionData?.conversation_sid ?? notification.messageId ?? undefined;
    return sid ? `/messaging?conversation=${sid}` : '/messaging';
  }
  if (notification.bookingId ?? notification.actionData?.booking_id) {
    return '/reservations';
  }
  if (notification.type === 'quote_received' || notification.actionData?.quote_id) {
    return '/quotes';
  }
  return null;
};

function withClientAudience(params: URLSearchParams): URLSearchParams {
  params.set('audience', 'client');
  return params;
}

export async function getNotifications(
  limit = 20,
  offset = 0,
  unreadOnly = false,
): Promise<{ notifications: AppNotification[]; total: number }> {
  const params = withClientAudience(
    new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      ...(unreadOnly && { unread_only: 'true' }),
    }),
  );

  const response = await fetch(`${notificationsUrl('/notifications')}?${params}`, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications: ${response.statusText}`);
  }
  const data = await response.json();
  const notifications = filterClientNotifications(data.notifications || []);
  return {
    notifications,
    total: data.total_returned ?? notifications.length,
  };
}

export async function getUnreadNotificationCount(): Promise<number> {
  try {
    // Ne pas se fier seul à /unread-count : le stage mobile peut ignorer audience=client.
    // On aligne le badge sur le même filtre que la liste (pro-only exclus).
    const { notifications } = await getNotifications(50, 0, true);
    return notifications.filter((n) => !n.isRead).length;
  } catch {
    return 0;
  }
}

/** Émet un nouveau total non lu pour la tab bar (après load / mark-read). */
export function publishUnreadNotificationCount(count: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('rintintin:notif-unread', { detail: Math.max(0, count) }),
  );
}

export async function markNotificationAsRead(notificationId: number): Promise<void> {
  const response = await fetch(
    notificationsUrl(`/notifications/${notificationId}/mark-read`),
    { method: 'POST', headers: getHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to mark notification as read: ${response.statusText}`);
  }
}

export async function markAllNotificationsAsRead(): Promise<void> {
  const params = withClientAudience(new URLSearchParams());
  const response = await fetch(
    `${notificationsUrl('/notifications/mark-all-read')}?${params}`,
    {
      method: 'POST',
      headers: getHeaders(),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to mark all as read: ${response.statusText}`);
  }
}
